/**
 * 诊断前端与 sidecar（console/diagnostics/sidecar）的接口契约：字段、枚举、错误码在前端只在这里定义一次。
 * 字段口径以 sidecar 为准（routers/ 与 services/ 里组装的字典），这里不重新定义含义。
 *
 * sidecar 不用统一信封：成功直接返回数据，失败是 FastAPI 的 `{"detail": "..."}` 加 HTTP 状态；
 * 要前端按它做事的失败，detail 是 `{"code": "...", "message": "...", ...}`（同入口的 reauth_required 写法）。
 * 前端在 api/client.ts 里把失败统一成 ApiError(code)，code 的取值见 ApiErrorCode。
 */

// === 错误码 ===

/**
 * 请求失败的 code。detail 里带 code 的用它（下面「查看权限」那几个）；没带的按 HTTP 状态归；界面只按 code 分支。
 * - `unauthenticated`（401）：没有本后台的通行证或已失效，去入口换一张
 * - `forbidden`（403）：这个接口不给当前角色（含 sidecar 的 `role_not_allowed`：角色不在放行名单里）
 * - `not_found`（404）
 * - `unavailable`（503）：认证服务或测试网络的库暂时连不上
 * - `server_error`（其它 5xx）、`bad_response`（其它 4xx 或响应不是 JSON）
 * - 请求根本没完成：`network_offline`、`server_unreachable`、`timeout`
 *
 * 查看权限（sidecar 的 access.py，契约 console/docs/specs/问题诊断查看权限.md）：
 * - `reason_required`（403）：要先登记这台设备当天的查看理由；detail 里带 `sn`（按读者投影：研发、客服是真 SN，
 *   履约、员工是假名）
 * - `invalid_reason`、`reason_text_required`、`reason_text_too_long`（400）：登记理由没过校验
 * - `access_log_unavailable`（503）：查看记录写不进去，按规定这次不给内容
 */
export type ApiErrorCode =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'unavailable'
  | 'server_error'
  | 'bad_response'
  | 'network_offline'
  | 'server_unreachable'
  | 'timeout'
  | 'reason_required'
  | 'invalid_reason'
  | 'reason_text_required'
  | 'reason_text_too_long'
  | 'access_log_unavailable';

/** detail 里带的、前端认的 code（其余一律按 HTTP 状态归） */
export const DETAIL_CODES: readonly ApiErrorCode[] = [
  'reason_required',
  'invalid_reason',
  'reason_text_required',
  'reason_text_too_long',
  'access_log_unavailable',
];

// === 当前用户（GET /api/console/me） ===

export interface Me {
  username: string;
  display_name: string;
  /** 一个人可以同时是研发和履约，判断一律用集合 */
  roles: string[];
  auth_at: number;
}

// === 查看权限（routers/access_api.py；契约 console/docs/specs/问题诊断查看权限.md） ===

/** 理由码。和 sidecar access.REASONS 是同一份，改一处要两处一起改 */
export type ReasonCode = 'troubleshooting' | 'support_case' | 'user_research' | 'feature_check' | 'other';

/**
 * 理由选项（汤祺 2026-10-07 定：直接选，留一个「其他」，选其他必须手写）。
 * `hint` 在选理由的面板里紧跟名字写在括号里、同一行（汤祺 10-08：不另起一行灰字）；查看记录里只写名字。
 */
export const REASONS: { code: ReasonCode; label: string; hint?: string }[] = [
  { code: 'troubleshooting', label: '排查问题' },
  { code: 'support_case', label: '处理客服或售后问题' },
  { code: 'user_research', label: '用户研究与产品分析' },
  { code: 'feature_check', label: '验证新功能或新版本' },
  { code: 'other', label: '其他', hint: '必须写明原因' },
];

/** 「其他」的说明最多几个字（同 sidecar access.REASON_TEXT_MAX） */
export const REASON_TEXT_MAX = 200;

/** 只登记线上设备：测网设备 2026-10-08 起不要理由、不进查看记录 */
export interface GrantRequest {
  sn: string;
  reason_code: ReasonCode;
  reason_text?: string;
}

export interface GrantResponse {
  sn: string;
  day: string;
  reasonCode: ReasonCode;
  reasonText: string | null;
  grantedAt: string;
}

/** 看过的内容种类（sidecar access.KINDS）。`test_conversation` 2026-10-08 起不再记，留着只为显示之前记下的行 */
export type ViewKind = 'conversation' | 'audio' | 'memory' | 'diary' | 'context' | 'evidence' | 'test_conversation' | 'biometrics';

export const VIEW_KIND_LABEL: Record<ViewKind, string> = {
  conversation: '对话',
  audio: '录音',
  memory: '记忆',
  diary: '日记',
  context: '上下文',
  evidence: '巡检证据',
  test_conversation: '测网对话',
  biometrics: '人脸与声纹',
};

/** 查看记录的一行：一个人一天看一台设备记一行（北京时间自然日） */
export interface AccessLogRow {
  day: string;
  username: string;
  displayName: string;
  /** 登记时的角色快照 */
  roles: string[];
  /** 管理员、研发、客服看真 SN，履约、员工看假名 */
  sn: string;
  /** null = 直接查看（管理员不要求理由；10-08 之前研发、客服也是） */
  reasonCode: ReasonCode | null;
  reasonText: string | null;
  grantedAt: string | null;
  /** 北京时间「2026-10-07 10:42:05」；只登记了理由、还没看内容时为 null */
  firstAt: string | null;
  lastAt: string | null;
  /** 请求次数（翻页、自动刷新各算一次） */
  views: number;
  kinds: ViewKind[];
}

export interface AccessLogQuery {
  from?: string;
  to?: string;
  user?: string;
  device?: string;
  page: number;
  pageSize: number;
}

export interface AccessLogResponse extends Paged<AccessLogRow> {
  retentionDays: number;
}

// === 设备列表（GET /api/dashboard/devices） ===

/**
 * 设备归属（website/docs/specs/设备归属分类.md 的 cohort 枚举）。unknown = 归属表里没有这台（契约里的故障状态）。
 * 设备列表不列 factory（工厂留样机），只能搜到
 */
export type Cohort = 'shop' | 'kickstarter' | 'beta' | 'kol' | 'gift' | 'partner' | 'internal' | 'factory' | 'stock' | 'unknown';
/** 设备列表的归属视图（汤祺 10-06） */
export type CohortView = 'all' | 'shop' | 'kickstarter' | 'beta' | 'internal';

export interface DeviceListItem {
  sn: string;
  // 机主：设备归属表（site-api）里我们自己登记的优先，没登记的是 App 里主要用户（管理员账号）的名字（汤祺 10-06）。
  // null：归属表取不到，或当前角色看不到；""：既没登记，App 账号也没填名字。
  owner: string | null;
  /** 机主从哪来：registry = 设备归属表登记的；app = App 里主要用户（管理员账号）的名字；null = 没有 */
  ownerSource: 'registry' | 'app' | null;
  /** null：归属数据源取不到 */
  cohort: Cohort | null;
  status: 'online' | 'offline';
  /** ISO 两位国家代码，页面按它显示中文国名；没有定位时为 "" */
  countryCode: string;
  /** 国家名（云端写的，有时英文有时中文）。字段名是历史错位：region 放国家、city 放州 / 省 */
  region: string;
  /** 州 / 省 */
  city: string;
  lastActive: string;
  totalConversations: number;
  totalMessages: number;
  activationDuration: number;
}

export type DeviceSortKey = 'sn' | 'lastActive' | 'totalMessages' | 'activationDuration';

export interface DeviceListQuery {
  search?: string;
  status?: 'online' | 'offline' | 'all';
  cohort?: CohortView;
  page: number;
  pageSize: number;
  sortBy: DeviceSortKey;
  sortDir: 'asc' | 'desc';
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DeviceListResponse extends Paged<DeviceListItem> {
  /** 视图页签的计数（搜索、在线条件生效之后，按归属分之前）；null = 归属取不到 */
  counts: Record<CohortView, number> | null;
}

// === 设备实时状态（GET /api/devices/{sn}/status） ===

export interface DeviceStatusData {
  device_status: string;
  battery_pct: number | null;
  power_status: string | null;
  network_status: string | null;
  wifi_rssi: number | null;
  volume: number | null;
  eye_brightness: number | null;
  eye_name: string | null;
  eye_series_name: string | null;
  sw_version: string | null;
  fw_version: string | null;
  language: string | null;
  time: string | null;
  device_id: string | null;
  online_status: string | null;
  is_online: boolean;
  ip: string | null;
  /** ISO 两位国家代码，页面按它显示中文国名 */
  country_code: string | null;
  country_name: string | null;
  region: string | null;
  city: string | null;
  timezone: string | null;
}

export interface BoundUser {
  userId: string;
  email: string;
  isAdmin: boolean;
  status: string;
  createdAt: string | null;
}

export interface DeviceStatusResponse {
  sn: string;
  /** null：这台设备还没有上报过状态 */
  status: DeviceStatusData | null;
  boundUsers?: BoundUser[];
}

// === 统计（GET /api/dashboard/devices/{sn}/history） ===

export interface DeviceHistory {
  totalConversations: number;
  totalMessages: number;
  totalUptime: number;
  totalTokens: number;
  dailyTrend: DailyDeviceTrend[];
}

export interface DailyDeviceTrend {
  date: string;
  conversations: number;
  messages: number;
  /** 那天没有调用记录时是 0 */
  avgLatency: number;
}

// === 人脸与声纹（GET …/faces、…/voiceprints） ===

export interface FaceRecord {
  id: string;
  speakerId: string;
  /** 归并后的编号（speaker_id_alias_final）；没归并过的就是 speakerId 自己 */
  finalSpeakerId: string;
  name: string;
  imageUrl: string;
  confidence: number;
  createdAt: string;
}

export interface VoiceprintRecord {
  id: string;
  speakerId: string;
  /** 归并后的编号（speaker_id_alias_final）；没归并过的就是 speakerId 自己 */
  finalSpeakerId: string;
  audioUrl: string;
  confidence: number;
  duration: number;
  createdAt: string;
}

// === 记忆（GET …/memory） ===

export interface PophieProfile {
  customName: string;
  isActivated: boolean;
  location: string;
  replyLanguage: string;
  longBehaviorRules: string;
}

export interface MemorySummaryItem {
  level: string; // day / week / month / core
  periodKey: string; // 2026-04-01 / 2026-W14 / 2026-04 / core
  content: string;
  wordCount: number;
  sourceCount: number;
  timeStart: string;
  timeEnd: string;
  version: number;
}

export interface LongTermMemory {
  eventId: string;
  description: string;
  importanceScore: number;
  happenTime: string;
  frequency: number;
}

export interface ShortTermMemory {
  id: string;
  memoryType: string;
  sessionId: string;
  roundStart: number;
  roundEnd: number;
  timeStart: string;
  timeEnd: string;
  summary: string;
}

export interface UserProfile {
  speakerId: string;
  name: string;
  nickname: string;
  // 假名版（履约、员工）是分桶字符串（"6-12"），看原样的角色是精确年龄。
  age: number | string | null;
  birthday: string;
  profileDesc: string;
  /**
   * 编号是归并后的 final（speaker_id_alias_final）：运行时只读这一份。
   * false 的是挂在旧编号（别名）上的画像，同一个人归并前留下的，不进 prompt
   */
  isFinal: boolean;
}

export interface MemoryData {
  longTerm: LongTermMemory[];
  shortTerm: ShortTermMemory[];
  memorySummary: MemorySummaryItem[];
  working: Record<string, unknown>;
  userProfiles: UserProfile[];
  pophieProfile: PophieProfile | null;
}

// === 日记（GET …/diaries） ===

export interface DiaryListItem {
  date: string;
  title: string;
  content: string;
  cover: string;
}

export interface DiaryListResponse extends Paged<DiaryListItem> {
  hasMore: boolean;
}

// === 对话 ===

/** 对话记录的一轮（GET …/conversation-history，第 1 页最新，页内从早到晚） */
export interface ConversationHistoryItem {
  filtered?: FilteredInput;
  timeBasis?: 'chat_time' | 'event_time_bj' | 'created_at' | 'unknown';
  id: string;
  time: string;
  speakerId: string;
  userText: string;
  llmText: string;
  asrAudio: string;
  ttsAudio: string;
  image: string;
  asrAudioFromRtc: string;
  traceId: string;
  inputType: number | null;
  reasoning: string | null;
  emotion: string | null;
  actionId: string | null;
}

export type ConversationHistoryResponse = Paged<ConversationHistoryItem> & {
  earliestTime: string | null;
  asOf: number;
  snapshotId: string;
  filteredSnapshotId?: string;
};

export interface ConversationFilter {
  date?: string;
  asOf?: number;
  snapshotId?: string;
  filteredSnapshotId?: string;
  includeFiltered?: boolean;
}

export type ConversationSearchResponse = Paged<ConversationSearchResult> & {
  earliestTime: string | null;
  asOf: number;
  snapshotId: string;
  filteredSnapshotId?: string;
};

export interface DeviceUsageInfo {
  activation: { sourceStatus: 'ok' | 'unavailable'; activatedAt: string | null };
  entitlement: {
    sourceStatus: 'ok' | 'unavailable';
    tier: string | null;
    state: string | null;
    expireAt: string | null;
  };
}

/** 对话搜索的一条（GET …/conversations?q=，从新到旧） */
export interface ConversationSearchResult {
  filtered?: FilteredInput;
  timeBasis?: 'chat_time' | 'event_time_bj' | 'created_at' | 'unknown';
  /** 名字叫 traceId，值其实是记录 id（conversation_record_log_online.id），和对话记录的 `id` 同一个值；
   *  「在对话中定位」拿它问 conversation-page。不是查上下文用的 trace_id。 */
  traceId: string;
  time: string;
  speakerId: string;
  userText: string;
  llmText: string;
  asrAudio: string;
  ttsAudio: string;
  image: string;
  asrAudioFromRtc: string;
  inputType: number | null;
  reasoning: string | null;
  emotion: string | null;
  actionId: string | null;
}

/** 一轮在对话记录里的第几页（GET …/conversation-page?id= 或 ?trace=） */
export interface ConversationPage {
  page: number;
  /** 这一轮的记录 id（同对话记录的 `id`）；按 trace 找时页面拿它高亮、展开 */
  id: string;
}

/** 一轮送给模型的上下文（GET …/conversation-context?turn_id=）。查不到时 sidecar 回空对象 */
export interface CallingContextDetail {
  user_input: string | null;
  prompt_key: string | null;
  prompt_label: string | null;
  robot_long_memory: string | null;
  robot_short_memory: string | null;
  robot_emotion: string | null;
  robot_status: string | null;
  current_time: string | null;
  current_location: string | null;
  live_scenario: string | null;
  history: string | null;
  history_length: number;
  session_id: string | null;
  speaker_id: string | null;
  prompt: string | null;
  llm_result: string | null;
}

// === 测网设备（routers/test_devices.py，读测试网络的库） ===

export interface TestDevice {
  sn: string;
  recordCount: number;
  /** 带时区的 ISO 串（UTC），显示前换成北京时间 */
  lastActive: string;
  firstActive: string;
  status: 'online' | 'offline' | 'unknown';
}

export interface TestTurn {
  id: string;
  /** 带时区的 ISO 串（UTC） */
  time: string;
  speakerId: string;
  userText: string;
  llmText: string;
  /** 模型写的理由（llm_result.reasoning）；没有时为 null */
  reasoning: string | null;
  /** turn_status 原文：completed / merged / success / …；没记时为 null */
  status: string | null;
}

/** 已落库的回复前过滤输入；不代表全部麦克风输入。音频为空时不能回听核验。 */
export interface FilteredInput {
  id: string;
  trace_id: string;
  filter_stage: string;
  filter_reason: string | null;
  asr_text: string | null;
  chat_time: number | null;
  duration_ms: number | null;
  sample_rate: number | null;
  origin_audio: string | null;
  clear_audio: string | null;
  audio_dropped_reason: string | null;
}
export interface FilteredInputsResponse {
  items: FilteredInput[];
  hasMore: boolean;
  nextBeforeId: string | null;
  date: string | null;
}
